import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  Tabs,
  Tab,
  Grid,
  CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StoreIcon from '@mui/icons-material/Store';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';
import { toast } from 'react-toastify';
import { api } from '../services/AuthService';
import { useAuth } from '../services/AuthService';

// O input datetime-local trabalha com a hora local do navegador. Usar
// toISOString() aqui mostrava uma hora UTC como se fosse local (Brasil: +3h),
// fazendo cupons novos começarem no futuro sem o administrador perceber.
const toLocalDateTimeInput = (date) => {
  const pad = (value) => String(value).padStart(2, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

function Promocoes() {
  const [tabValue, setTabValue] = useState(0);
  const [promocoes, setPromocoes] = useState([]);
  const [lanchonetes, setLanchonetes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [currentLanchonete, setCurrentLanchonete] = useState(null);
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [benefitDialogOpen, setBenefitDialogOpen] = useState(false);
  
  // Estados de formulário
  const [formData, setFormData] = useState({
    code: '',
    title: '',
    description: '',
    lanchonete_id: '',
    discount_type: 'FIXED_VALUE',
    discount_value: '0',
    applies_to: 'PRODUCTS',
    min_order_value: '0',
    max_discount_value: '',
    start_date: toLocalDateTimeInput(new Date()),
    end_date: toLocalDateTimeInput(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)),
    usage_limit: '',
    active: true,
    promotion_kind: 'DISCOUNT',
    trigger_product_ids: [],
    trigger_quantity: '5',
    gift_product_id: '',
    gift_quantity: '1',
    max_gifts_per_order: '1'
  });

  const [benefitData, setBenefitData] = useState({
    user_id: '',
    duration_days: '7'
  });

  const { isAdminMaster, isAdminLanchonete, getLanchoneteId } = useAuth();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [promocoesResponse, lanchonetesResponse] = await Promise.all([
        api.get('/promocoes'),
        api.get('/lanchonetes')
      ]);
      setPromocoes(promocoesResponse.data);
      setLanchonetes(lanchonetesResponse.data);

      if (isAdminMaster()) {
        const usersResponse = await api.get('/users');
        // Apenas clientes comuns
        setUsuarios(usersResponse.data.filter(u => u.user_type === 'client'));
      }

      if (isAdminLanchonete()) {
        const lanchoneteId = getLanchoneteId();
        if (lanchoneteId) {
          const lanchoneteRes = await api.get(`/lanchonetes/${lanchoneteId}`);
          setCurrentLanchonete(lanchoneteRes.data);
          const produtosRes = await api.get('/produtos', { params: { lanchonete_id: lanchoneteId } });
          setProdutos((produtosRes.data || []).filter((produto) => produto.available));
          // Configurar valor padrão do applies_to para parceiro
          setFormData(prev => ({
            ...prev,
            applies_to: 'PRODUCTS'
          }));
        }
      }
    } catch (error) {
      toast.error('Erro ao carregar dados de promoções.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [isAdminMaster, isAdminLanchonete, getLanchoneteId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!isAdminMaster() || !formData.lanchonete_id || formData.promotion_kind !== 'BUY_X_GET_Y') {
      if (isAdminMaster()) setProdutos((current) => current.length ? [] : current);
      return;
    }
    let cancelled = false;
    api.get('/produtos', { params: { lanchonete_id: formData.lanchonete_id } })
      .then((response) => {
        if (!cancelled) setProdutos((response.data || []).filter((produto) => produto.available));
      })
      .catch(() => {
        if (!cancelled) {
          setProdutos((current) => current.length ? [] : current);
          toast.error('Não foi possível carregar os produtos da lanchonete selecionada.');
        }
      });
    return () => { cancelled = true; };
  }, [formData.lanchonete_id, formData.promotion_kind, isAdminMaster]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenDialog = () => {
    const isMaster = isAdminMaster();
    setFormData({
      code: '',
      title: '',
      description: '',
      lanchonete_id: '',
      discount_type: isMaster ? 'FREE_DELIVERY' : 'FIXED_VALUE',
      discount_value: '0',
      applies_to: isMaster ? 'DELIVERY_FEE' : 'PRODUCTS',
      min_order_value: '0',
      max_discount_value: '',
      start_date: toLocalDateTimeInput(new Date()),
      end_date: toLocalDateTimeInput(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)),
      usage_limit: '',
      active: true,
      promotion_kind: 'DISCOUNT',
      trigger_product_ids: [],
      trigger_quantity: '5',
      gift_product_id: '',
      gift_quantity: '1',
      max_gifts_per_order: '1'
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleOpenBenefitDialog = () => {
    setBenefitData({
      user_id: '',
      duration_days: '7'
    });
    setBenefitDialogOpen(true);
  };

  const handleCloseBenefitDialog = () => {
    setBenefitDialogOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };

      if (name === 'lanchonete_id') {
        updated.trigger_product_ids = [];
        updated.gift_product_id = '';
      }

      // Regra de Negócio: Se o tipo for Frete Grátis, forçar applies_to para DELIVERY_FEE e valor do desconto para 0
      if (name === 'discount_type' && value === 'FREE_DELIVERY') {
        updated.applies_to = 'DELIVERY_FEE';
        updated.discount_value = '0';
      }

      return updated;
    });
  };

  const handleBenefitInputChange = (e) => {
    const { name, value } = e.target;
    setBenefitData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        lanchonete_id: isAdminMaster() && formData.lanchonete_id ? Number(formData.lanchonete_id) : null,
        discount_value: Number(formData.discount_value),
        min_order_value: Number(formData.min_order_value),
        max_discount_value: formData.max_discount_value ? Number(formData.max_discount_value) : null,
        usage_limit: formData.usage_limit ? Number(formData.usage_limit) : null,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString(),
        code: formData.code.trim() ? formData.code.trim().toUpperCase() : null
      };

      if (formData.promotion_kind === 'BUY_X_GET_Y') {
        payload.discount_type = 'FIXED_VALUE';
        payload.discount_value = 0;
        payload.applies_to = 'PRODUCTS';
        payload.code = null;
        payload.trigger_product_ids = formData.trigger_product_ids.map(Number);
        payload.trigger_quantity = Number(formData.trigger_quantity);
        payload.gift_product_id = Number(formData.gift_product_id);
        payload.gift_quantity = Number(formData.gift_quantity);
        payload.max_gifts_per_order = formData.max_gifts_per_order ? Number(formData.max_gifts_per_order) : null;
        if (!payload.trigger_product_ids.length || !payload.gift_product_id || payload.trigger_quantity < 1 || payload.gift_quantity < 1) {
          toast.error('Selecione os produtos participantes, a quantidade e o brinde.');
          return;
        }
      }

      // Validar regras de negócio no frontend antes de submeter
      if (isAdminLanchonete()) {
        const isAppLogistics = String(currentLanchonete?.delivery_mode).toUpperCase() === 'APP';
        if (isAppLogistics && (payload.applies_to === 'DELIVERY_FEE' || payload.discount_type === 'FREE_DELIVERY')) {
          toast.error('Sua lanchonete utiliza a entrega do App. Você não pode dar descontos na taxa de entrega!');
          return;
        }
      } else if (isAdminMaster() && ['PRODUCTS', 'BOTH'].includes(payload.applies_to) && !payload.lanchonete_id) {
        toast.error('Para dar desconto em produtos, selecione a lanchonete participante.');
        return;
      }

      await api.post('/promocoes', payload);
      toast.success('Promoção/Cupom criado com sucesso!');
      handleCloseDialog();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao criar promoção.');
    }
  };

  const handleBenefitSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/promocoes/beneficio', {
        user_id: Number(benefitData.user_id),
        duration_days: Number(benefitData.duration_days)
      });
      toast.success('Benefício de Frete Grátis concedido com sucesso!');
      handleCloseBenefitDialog();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao conceder benefício.');
    }
  };

  const handleToggleStatus = async (id, active) => {
    try {
      await api.put(`/promocoes/${id}/status`, { active });
      toast.success(`Cupom ${active ? 'ativado' : 'desativado'} com sucesso!`);
      loadData();
    } catch (error) {
      toast.error('Erro ao alterar status da promoção.');
    }
  };

  const handleDeletePromo = async (promo) => {
    if (!window.confirm(`Deseja realmente apagar o cupom "${promo.code || promo.title}"?`)) {
      return;
    }

    try {
      await api.delete(`/promocoes/${promo.id}`);
      toast.success('Cupom apagado com sucesso!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao apagar cupom.');
    }
  };

  const handleDeactivateBenefit = async (userId) => {
    if (window.confirm('Deseja realmente remover o benefício de frete grátis deste cliente?')) {
      try {
        await api.delete(`/promocoes/beneficio/${userId}`);
        toast.success('Benefício de frete grátis removido!');
        loadData();
      } catch (error) {
        toast.error('Erro ao desativar benefício.');
      }
    }
  };

  const getDiscountTypeLabel = (type) => {
    switch (type) {
      case 'PERCENTAGE': return 'Porcentagem (%)';
      case 'FIXED_VALUE': return 'Valor Fixo (R$)';
      case 'FREE_DELIVERY': return 'Frete Grátis';
      default: return type;
    }
  };

  const getAppliesToLabel = (applies) => {
    switch (applies) {
      case 'PRODUCTS': return 'Lanches / Produtos';
      case 'DELIVERY_FEE': return 'Taxa de Entrega';
      case 'BOTH': return 'Ambos (Produtos e Entrega)';
      default: return applies;
    }
  };

  const getLanchoneteName = (lanchoneteId) => {
    const lanchonete = lanchonetes.find(l => Number(l.id) === Number(lanchoneteId));
    return lanchonete ? lanchonete.name : 'Desconhecida';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const isLanchoneteAppLogistics = isAdminLanchonete() && String(currentLanchonete?.delivery_mode).toUpperCase() === 'APP';

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Gestão de Promoções e Cupons
        </Typography>
        {tabValue === 0 && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            Nova Promoção / Cupom
          </Button>
        )}
        {tabValue === 1 && isAdminMaster() && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<StarIcon />}
            onClick={handleOpenBenefitDialog}
          >
            Conceder Frete Grátis
          </Button>
        )}
      </Box>

      {isAdminMaster() ? (
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab label="Promoções e Cupons ativos" icon={<LocalOfferIcon />} iconPosition="start" />
          <Tab label="Clientes com Frete Grátis" icon={<PersonIcon />} iconPosition="start" />
        </Tabs>
      ) : (
        isLanchoneteAppLogistics && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            ⚠️ <strong>Atenção:</strong> Sua lanchonete utiliza os motoboys da plataforma (logística APP). Você está restrito a criar promoções exclusivamente nos produtos/lanches. Promoções ou cupons de desconto na taxa de entrega são bloqueados.
          </Alert>
        )
      )}

      {tabValue === 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Código / Título</TableCell>
                <TableCell>Tipo de Desconto</TableCell>
                <TableCell>Valor</TableCell>
                <TableCell>Aplica-se a</TableCell>
                <TableCell>Criador / Contexto</TableCell>
                <TableCell>Regras e Limites</TableCell>
                <TableCell>Expiração</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Acoes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {promocoes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="textSecondary" sx={{ py: 2 }}>
                      Nenhuma promoção ou cupom cadastrado.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                promocoes.map((promo) => (
                  <TableRow key={promo.id}>
                    <TableCell>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {promo.code ? <Chip label={promo.code} color="primary" variant="outlined" size="small" sx={{ mr: 1 }} /> : null}
                        {promo.title}
                      </Typography>
                      {promo.description && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          {promo.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={promo.promotion_kind === 'BUY_X_GET_Y' ? 'Compre e ganhe' : getDiscountTypeLabel(promo.discount_type)} color="secondary" size="small" />
                    </TableCell>
                    <TableCell>
                      {promo.promotion_kind === 'BUY_X_GET_Y' ? (
                        `${promo.gift_quantity || 1} brinde(s)`
                      ) : promo.discount_type === 'FREE_DELIVERY' ? (
                        'Total'
                      ) : promo.discount_type === 'PERCENTAGE' ? (
                        `${promo.discount_value}%`
                      ) : (
                        `R$ ${Number(promo.discount_value).toFixed(2)}`
                      )}
                    </TableCell>
                    <TableCell>{getAppliesToLabel(promo.applies_to)}</TableCell>
                    <TableCell>
                      {promo.creator_type === 'ADMIN_MASTER' ? (
                        <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
                          <Chip icon={<StarIcon />} label="Admin Master" color="error" size="small" />
                          <Typography variant="caption">
                            {promo.lanchonete_id ? getLanchoneteName(promo.lanchonete_id) : 'Todas as lanchonetes'}
                          </Typography>
                        </Box>
                      ) : (
                        <Box display="flex" alignItems="center" gap={1}>
                          <StoreIcon fontSize="small" color="action" />
                          <Typography variant="body2">{getLanchoneteName(promo.lanchonete_id)}</Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" display="block">
                        {promo.promotion_kind === 'BUY_X_GET_Y'
                          ? `A cada ${promo.trigger_quantity} produto(s) participante(s)`
                          : `Pedido mínimo: R$ ${Number(promo.min_order_value).toFixed(2)}`}
                      </Typography>
                      {promo.usage_limit && (
                        <Typography variant="caption" display="block">
                          Uso Geral: {promo.used_count || 0} / {promo.usage_limit}
                        </Typography>
                      )}
                      {promo.max_discount_value && (
                        <Typography variant="caption" display="block">
                          Desconto Max: R$ {Number(promo.max_discount_value).toFixed(2)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(promo.end_date).toLocaleDateString('pt-BR')} às {new Date(promo.end_date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(promo.active)}
                            onChange={(e) => handleToggleStatus(promo.id, e.target.checked)}
                            color="primary"
                          />
                        }
                        label={Boolean(promo.active) ? 'Ativo' : 'Inativo'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="error"
                        onClick={() => handleDeletePromo(promo)}
                        aria-label="Apagar cupom"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tabValue === 1 && isAdminMaster() && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Telefone</TableCell>
                    <TableCell>Expira em</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usuarios.filter(u => u.has_free_delivery || u.free_delivery_active).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="textSecondary" sx={{ py: 2 }}>
                          Nenhum cliente com benefício de frete grátis ativo.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    usuarios.filter(u => u.has_free_delivery || u.free_delivery_active).map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.id}</TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {user.name}
                          </Typography>
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.phone || '-'}</TableCell>
                        <TableCell>
                          <Typography color="error" variant="body2">
                            {user.benefit_expiration ? new Date(user.benefit_expiration).toLocaleDateString('pt-BR') : 'Período Ativo'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            color="error"
                            onClick={() => handleDeactivateBenefit(user.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      )}

      {/* Dialog de Criação de Promoção */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Nova Promoção ou Cupom de Desconto</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="title"
                  label="Título da Promoção"
                  placeholder="Ex: Desconto de Inauguração"
                  value={formData.title}
                  onChange={handleInputChange}
                  fullWidth
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  name="description"
                  label="Descrição detalhada"
                  placeholder="Ex: Ganhe R$ 10 de desconto em pedidos acima de R$ 50"
                  value={formData.description}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Modelo da promoção</InputLabel>
                  <Select name="promotion_kind" value={formData.promotion_kind} onChange={handleInputChange} label="Modelo da promoção">
                    <MenuItem value="DISCOUNT">Desconto ou cupom</MenuItem>
                    <MenuItem value="BUY_X_GET_Y">Compre uma quantidade e ganhe um brinde</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {formData.promotion_kind === 'BUY_X_GET_Y' && (
                <>
                  <Grid item xs={12}>
                    <Alert severity="success">
                      Exemplo: ao comprar a quantidade definida de produtos participantes, o sistema adiciona o brinde automaticamente ao pedido.
                    </Alert>
                  </Grid>
                  {isAdminMaster() && !formData.lanchonete_id && (
                    <Grid item xs={12}><Alert severity="info">Escolha a lanchonete participante para carregar os produtos.</Alert></Grid>
                  )}
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Produtos que contam para a promoção</InputLabel>
                      <Select
                        multiple name="trigger_product_ids" value={formData.trigger_product_ids}
                        onChange={handleInputChange} label="Produtos que contam para a promoção"
                        renderValue={(ids) => ids.map((id) => produtos.find((p) => Number(p.id) === Number(id))?.name).filter(Boolean).join(', ')}
                      >
                        {produtos.map((produto) => <MenuItem key={produto.id} value={produto.id}>{produto.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField name="trigger_quantity" label="Quantidade mínima" type="number" value={formData.trigger_quantity} onChange={handleInputChange} inputProps={{ min: 1 }} fullWidth required />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Produto de brinde</InputLabel>
                      <Select name="gift_product_id" value={formData.gift_product_id} onChange={handleInputChange} label="Produto de brinde">
                        {produtos.map((produto) => <MenuItem key={produto.id} value={produto.id}>{produto.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField name="gift_quantity" label="Quantidade do brinde" type="number" value={formData.gift_quantity} onChange={handleInputChange} inputProps={{ min: 1 }} fullWidth required />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField name="max_gifts_per_order" label="Máximo de brindes por pedido" type="number" value={formData.max_gifts_per_order} onChange={handleInputChange} inputProps={{ min: 1 }} fullWidth helperText="Vazio = sem limite" />
                  </Grid>
                </>
              )}

              {formData.promotion_kind !== 'BUY_X_GET_Y' && (<>
              <Grid item xs={6}>
                <TextField
                  name="code"
                  label="Código do Cupom"
                  placeholder="Ex: LANCHE10"
                  value={formData.code}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  helperText="O cliente informa este código no carrinho. Promoções com brinde são aplicadas automaticamente pela loja."
                />
              </Grid>

              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de Desconto</InputLabel>
                  <Select
                    name="discount_type"
                    value={formData.discount_type}
                    onChange={handleInputChange}
                    label="Tipo de Desconto"
                  >
                    {!isAdminMaster() && <MenuItem value="FIXED_VALUE">Valor Fixo (R$)</MenuItem>}
                    {!isAdminMaster() && <MenuItem value="PERCENTAGE">Porcentagem (%)</MenuItem>}
                    {(isAdminMaster() || !isLanchoneteAppLogistics) && (
                      <MenuItem value="FREE_DELIVERY">Frete Grátis</MenuItem>
                    )}
                    {isAdminMaster() && <MenuItem value="FIXED_VALUE">Desconto Fixo (R$)</MenuItem>}
                    {isAdminMaster() && <MenuItem value="PERCENTAGE">Desconto Percentual (%)</MenuItem>}
                  </Select>
                </FormControl>
              </Grid>

              {formData.discount_type !== 'FREE_DELIVERY' && (
                <Grid item xs={6}>
                  <TextField
                    name="discount_value"
                    label={formData.discount_type === 'PERCENTAGE' ? "Porcentagem (%)" : "Valor do Desconto (R$)"}
                    type="number"
                    value={formData.discount_value}
                    onChange={handleInputChange}
                    fullWidth
                    required
                  />
                </Grid>
              )}

              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Aplica-se a</InputLabel>
                  <Select
                    name="applies_to"
                    value={formData.applies_to}
                    onChange={handleInputChange}
                    label="Aplica-se a"
                    disabled={formData.discount_type === 'FREE_DELIVERY' || isLanchoneteAppLogistics}
                  >
                    <MenuItem value="PRODUCTS">Lanches / Produtos</MenuItem>
                    {!isLanchoneteAppLogistics && <MenuItem value="DELIVERY_FEE">Taxa de Entrega</MenuItem>}
                    {isAdminMaster() && <MenuItem value="BOTH">Produtos e Entrega</MenuItem>}
                  </Select>
                </FormControl>
              </Grid>

              {isAdminMaster() && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="promo-store-label">Lanchonete participante</InputLabel>
                    <Select
                      labelId="promo-store-label"
                      name="lanchonete_id"
                      value={formData.lanchonete_id}
                      onChange={handleInputChange}
                      label="Lanchonete participante"
                    >
                      <MenuItem value="">Todas as lanchonetes</MenuItem>
                      {lanchonetes.map((store) => (
                        <MenuItem key={store.id} value={store.id}>{store.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {isAdminMaster() && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    O desconto reduz o total cobrado do pedido. Para descontos em produtos, selecione a lanchonete participante e combine previamente como o valor será custeado.
                  </Alert>
                </Grid>
              )}

              <Grid item xs={6}>
                <TextField
                  name="min_order_value"
                  label="Valor Mínimo do Pedido (R$)"
                  type="number"
                  value={formData.min_order_value}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  name="usage_limit"
                  label="Limite de Usos Gerais (Opcional)"
                  type="number"
                  placeholder="Ilimitado"
                  value={formData.usage_limit}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>

              {formData.discount_type === 'PERCENTAGE' && (
                <Grid item xs={12}>
                  <TextField
                    name="max_discount_value"
                    label="Desconto Máximo Permitido (R$ - Opcional)"
                    type="number"
                    placeholder="Sem limite"
                    value={formData.max_discount_value}
                    onChange={handleInputChange}
                    fullWidth
                  />
                </Grid>
              )}
              </>)}

              <Grid item xs={6}>
                <TextField
                  name="start_date"
                  label="Inicia em"
                  type="datetime-local"
                  value={formData.start_date}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  name="end_date"
                  label="Expira em"
                  type="datetime-local"
                  value={formData.end_date}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      name="active"
                      checked={formData.active}
                      onChange={handleInputChange}
                      color="primary"
                    />
                  }
                  label="Promoção Ativa imediatamente"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained" color="primary">Criar</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog de Benefício Frete Grátis (Admin Master) */}
      <Dialog open={benefitDialogOpen} onClose={handleCloseBenefitDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Conceder Isenção de Frete Grátis</DialogTitle>
        <form onSubmit={handleBenefitSubmit}>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2}>
              <FormControl fullWidth required>
                <InputLabel>Selecionar Cliente</InputLabel>
                <Select
                  name="user_id"
                  value={benefitData.user_id}
                  onChange={handleBenefitInputChange}
                  label="Selecionar Cliente"
                >
                  {usuarios.map(user => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                name="duration_days"
                label="Duração da Isenção (dias)"
                type="number"
                value={benefitData.duration_days}
                onChange={handleBenefitInputChange}
                fullWidth
                required
                inputProps={{ min: 1 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseBenefitDialog}>Cancelar</Button>
            <Button type="submit" variant="contained" color="secondary">Conceder</Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default Promocoes;
